import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons'
import { Text } from '@mantine/core'
import React from 'react'

import {
  useMutationUpdateBooking,
  useQueryGetAllBookingProductsUnavailable,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreItemAvailability,
  type CoreBookingPayload,
  type ParameterID,
} from '@flecto/types'
import { CardList, Modal, useNotifications } from '@flecto/ui'

export type ModalBookingProductsUnavailableProps = {
  bookingId?: ParameterID
}

export type CoreItemAvailabilityLocal = CoreItemAvailability & {
  original_remaining_quantity: number
}

export function ModalBookingProductsUnavailable({
  bookingId,
}: ModalBookingProductsUnavailableProps) {
  const { t: tShared } = useTranslation('shared', { keyPrefix: 'shared' })
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })

  const { showNotification } = useNotifications()

  const { mutateAsync: updateBooking, isLoading: isUpdating } =
    useMutationUpdateBooking()

  const { data: unavailableItems } = useQueryGetAllBookingProductsUnavailable({
    bookingId,
  })

  const handleSubmit = async () => {
    try {
      const booking: CoreBookingPayload = {
        booking_items_attributes: unavailableItems.map((item) => ({
          id: item?.id,
          force_quantity_available: true,
        })),
      }

      await updateBooking({
        bookingId,
        booking,
        options: {
          force_quantity_available: true,
        },
      })

      showNotification({
        type: 'success',
        message: t('notification_update_booking_items_success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_update_booking_items_error'),
      })
    }
  }

  return (
    <Modal size='md'>
      <Modal.Close />
      <Modal.Icon icon={faExclamationCircle} color='red' />
      <Modal.Title>{t('modal_booking_items_unavailable_title')}</Modal.Title>
      <Modal.Subtitle>
        {t('modal_booking_items_unavailable_subtitle')}
      </Modal.Subtitle>
      <Modal.Content>
        <CardList variant='space-between'>
          {unavailableItems.map((item) => (
            <CardList.Item key={item?.id}>
              <CardList.ItemLabel>
                {item?.product?.title ?? ''}
              </CardList.ItemLabel>
              <CardList.ItemValue>
                <Text color='red'>{tShared('unavailable')}</Text>
              </CardList.ItemValue>
            </CardList.Item>
          ))}
        </CardList>
      </Modal.Content>
      <Modal.Cancel>{t('label_back_to_booking')}</Modal.Cancel>
      <Modal.Submit loading={isUpdating} onClick={handleSubmit} color='red'>
        {t('label_reset_and_proceed')}
      </Modal.Submit>
    </Modal>
  )
}
